/**
 * Inbox message integrity helper — defensive layer.
 *
 * 11/02/2026 (suite à retour user prod « bug message mixés ») — Détecte
 * les bodies de messages dans l'inbox qui présentent des signes évidents
 * de contamination (deux templates mélangés). Patterns observés en prod :
 *
 *   • Body contenant BOTH a thanks-after-raise signature (« Merci
 *     infiniment / sincèrement pour cette revalorisation ») AND
 *     a tender narrative fragment (« Conditions client », « €/jour/véhicule »
 *     ou « Durée imposée »).
 *   • Body avec un placeholder résiduel non substitué (`{xxxName}`,
 *     `{amount}`, `{days}`).
 *
 * Quand un message corrompu est détecté, on tente une réparation :
 *   1. Re-instancier le body depuis le template d'origine si `templateId`
 *      est connu (template trouvé dans MESSAGE_TEMPLATES).
 *   2. Sinon, on flag le message avec `__corrupted: true` et on tronque
 *      le body à la première occurrence d'un fragment intrus.
 *
 * Aucune dépendance Zustand : c'est un helper PUR appelé par
 * `onRehydrateStorage` (gameStore.js) au démarrage de l'application.
 */
import { MESSAGE_TEMPLATES } from '../../data/messageTemplates';

// Signatures de contamination détectées en prod.
// Chaque entrée est une signature unique appartenant à UN SEUL template
// canonique. Si un body matche 2+ signatures distinctes, on considère qu'il
// s'agit d'un mélange (corruption).
const TEMPLATE_SIGNATURES = [
  // sys-welcome-01 (bienvenue système)
  { name: 'sys-welcome', re: /messagerie est désormais active/i },
  // Clara Fontana welcome (BNB Mariba, coreSlice)
  { name: 'clara-welcome', re: /situation chez BNB Mariba|Niveau 1 — Client privilégié/i },
  // Thanks-after-raise (drv-thanks-*)
  { name: 'thanks-after-raise', re: /Merci infiniment pour cette revalorisation|Merci sincèrement pour cette revalorisation|la reconnaissance que je méritais/i },
  // Tender announce / expiry / won
  { name: 'tender', re: /Conditions client|€\/jour\/véhicule|Durée imposée/i },
  // Monthly / annual report memos
  { name: 'monthly-report', re: /Bilan mensuel|Chiffre d'affaires du mois/i },
  // Sagesse de Maurice (post-liquidation restart)
  { name: 'maurice-wisdom', re: /Sagesse de l'échec/i },
];

// (Legacy exports gardés pour rétrocompat des tests existants — synonymes
// de sous-ensembles de TEMPLATE_SIGNATURES.)
const THANKS_SIGNATURES = [
  /Merci infiniment pour cette revalorisation/i,
  /Merci sincèrement pour cette revalorisation/i,
  /la reconnaissance que je méritais/i,
];

const TENDER_FRAGMENTS = [
  /Conditions client\s*:/i,
  /€\/jour\/véhicule/i,
  /Durée imposée/i,
  // Pattern observé en prod : « **N € Conditions client :** » rendu comme
  // un seul bloc gras combinant montant + label.
  /\d[\s\u00A0]*€[\s\u00A0]*Conditions/i,
];

const PLACEHOLDER_RE = /\{(clientName|amount|days|baseDailyRevenue|durationDays|requiresFleet)\}/;

/**
 * Returns true if the body shows the « thanks + tender » mix pattern OR
 * leaks an unfilled placeholder OR contient les signatures de 2+ templates
 * distincts (mixage détecté par comparaison de signatures uniques).
 *
 * 12/02/2026 (fix user prod, screenshots messages Nadia/Sophie/Tristan
 * contaminés) — On détecte aussi le pattern « nom en gras répété » qui
 * apparaît quand la contamination inclut plusieurs bullets `**{name}**`
 * concaténés sans séparateur (ex : `**Isabelle MorinIsabelle Morin**`).
 * Signature : au moins 2 mots capitalisés répétés à l'intérieur d'un
 * même `**...**` non séparés par espace.
 */
const REPEATED_BOLD_NAME_RE = /\*\*([A-ZÀ-Ö][a-zà-ö]+(?:\s[A-ZÀ-Ö][a-zà-ö]+)*)\1+\*\*/;

export function isCorruptedMessageBody(body) {
  if (!body || typeof body !== 'string') return false;
  // Legacy path : thanks + tender combo (Lot N suite fix).
  const hasThanks = THANKS_SIGNATURES.some(re => re.test(body));
  const hasTender = TENDER_FRAGMENTS.some(re => re.test(body));
  if (hasThanks && hasTender) return true;
  if (PLACEHOLDER_RE.test(body)) return true;
  // 11/02/2026 — Nouvelle détection généralisée : 2+ templates canoniques
  // mixés. Couvre par ex. le cas prod « sys-welcome + Clara Fontana ».
  const matchedNames = TEMPLATE_SIGNATURES
    .filter(sig => sig.re.test(body))
    .map(sig => sig.name);
  if (matchedNames.length >= 2) return true;
  // 12/02/2026 — Nom en gras répété (contamination bullets driver pool).
  if (REPEATED_BOLD_NAME_RE.test(body)) return true;
  return false;
}

/**
 * Simple placeholder substitution copy (single-pass), kept self-contained
 * so the helper has no circular dep with messageSlice.
 */
function fillPlaceholdersSafe(str, ctx) {
  return String(str || '').replace(/\{(\w+)\}/g, (_, k) =>
    ctx[k] != null ? ctx[k] : `{${k}}`
  );
}

/**
 * Attempt to repair a corrupted message :
 *   • Find its template via `templateId`.
 *   • Re-render subject + body from the template with the company name
 *     and any context we can recover (driverName from sender, etc.).
 *
 * Returns a new message object (or the original if nothing to do).
 */
export function repairCorruptedMessage(msg, companyName = 'SCD') {
  if (!msg || !isCorruptedMessageBody(msg.body)) return msg;
  const tpl = msg.templateId
    ? MESSAGE_TEMPLATES.find(t => t.id === msg.templateId)
    : null;
  if (!tpl) {
    // Defensive : truncate at first known intrusion to surface a readable
    // message rather than the garbled mix.
    const cutMarkers = ['**Conditions client', '€/jour/véhicule', '**Bricoland'];
    let cutAt = msg.body.length;
    for (const marker of cutMarkers) {
      const idx = msg.body.indexOf(marker);
      if (idx > 0 && idx < cutAt) cutAt = idx;
    }
    return {
      ...msg,
      body: msg.body.slice(0, cutAt).trim()
        + '\n\n[Message restauré automatiquement — contenu original incomplet]',
      __corrupted: true,
    };
  }
  // Recover ctx from msg fields the inbox typically preserves.
  const ctx = {
    companyName,
    driverName: msg.senderType === 'driver' ? (msg.senderName || '').replace(/\s*\(.*\)\s*/, '') : '',
    name: msg.senderType === 'driver' ? (msg.senderName || '').replace(/\s*\(.*\)\s*/, '') : '',
  };
  return {
    ...msg,
    subject: fillPlaceholdersSafe(tpl.subjectTemplate || msg.subject, ctx),
    body: fillPlaceholdersSafe(tpl.bodyTemplate || msg.body, ctx),
    __repaired: true,
  };
}

/**
 * Scan the persisted inbox and repair every detected corruption.
 * Returns `{ repairedInbox, repairedCount }` so the caller can log/notify.
 */
export function repairInbox(inbox, companyName) {
  if (!Array.isArray(inbox)) return { repairedInbox: inbox, repairedCount: 0 };
  let repairedCount = 0;
  const repairedInbox = inbox.map(m => {
    if (isCorruptedMessageBody(m.body)) {
      repairedCount += 1;
      return repairCorruptedMessage(m, companyName);
    }
    return m;
  });
  return { repairedInbox, repairedCount };
}
